/**
 * Edpies — Practice Hub.
 *
 * Re-organises Practice Papers by Exam Type FIRST, then subject, so
 * students preparing for a specific board (Sutton SET, GL, CEM, ISEB)
 * land on the right surface instead of trying to guess from a subject
 * grid. Each board card deep-links into the existing AssessmentFactory
 * / PaperLibrary flow with the right query params.
 *
 * Stage-aware: shows 11+ boards for Year 5/6, 7+ boards for Year 1/2,
 * GCSE boards for Year 10/11. A "Skill Builder" card always appears
 * so students who just want general practice still have a clear entry.
 */

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import TileEmblem from '@/components/TileEmblem';
import {
  ArrowLeft, ChevronRight, GraduationCap, School, BookOpen,
  Sparkles, Brain, Flag,
} from 'lucide-react';

const stageOf = (user) => {
  const profile = user?.studentProfile || {};
  const target = String(user?.examTarget || profile.examTarget || profile.examGoal || '').toLowerCase();
  const yg = String(user?.yearGroup || profile.yearGroup || '').toLowerCase();
  if (target === '11+' || target === '11plus' || target === '11-plus') return '11-plus';
  if (target === '7+' || target === '7plus' || target === '7-plus') return '7-plus';
  if (target === 'gcse' || target === 'ks4') return 'gcse';
  if (/year[\s-]?[12]\b/.test(yg)) return '7-plus';
  if (/year[\s-]?[56]\b/.test(yg)) return '11-plus';
  if (/year[\s-]?1[01]\b/.test(yg) || yg.includes('gcse') || yg.includes('ks4')) return 'gcse';
  return '11-plus';
};

const BOARDS_BY_STAGE = {
  '11-plus': [
    {
      id: 'sutton-set',
      title: 'Sutton SET',
      subtitle: 'Sutton Schools Examination Test — Stages 1 & 2',
      icon: School,
      accent: 'from-violet-500 to-indigo-600',
      pattern: 'bars',
      stages: [
        { label: 'Stage 1', sub: 'Standardised Maths + English papers',
          link: '/practice/board/sutton/1' },
        { label: 'Stage 2', sub: 'Reasoning + Creative writing',
          link: '/practice/board/sutton/2' },
      ],
    },
    {
      id: 'gl-assessment',
      title: 'GL Assessment',
      subtitle: 'Used by 75 % of UK grammar schools',
      icon: Flag,
      accent: 'from-rose-500 to-pink-600',
      pattern: 'rings',
      link: '/practice/board/gl',
    },
    {
      id: 'cem',
      title: 'CEM',
      subtitle: 'Centre for Evaluation & Monitoring — Durham',
      icon: Brain,
      accent: 'from-emerald-500 to-teal-600',
      pattern: 'waves',
      link: '/practice/board/cem',
    },
    {
      id: 'iseb',
      title: 'Independent Schools (ISEB)',
      subtitle: 'Common Pre-Test & ISEB style papers',
      icon: GraduationCap,
      accent: 'from-amber-500 to-orange-500',
      pattern: 'dots',
      link: '/practice/board/iseb',
    },
  ],
  '7-plus': [
    {
      id: 'school-entrance',
      title: 'School Entrance',
      subtitle: '7+ entry papers — English, Maths, Reasoning',
      icon: School,
      accent: 'from-violet-500 to-indigo-600',
      pattern: 'waves',
      link: '/subjects/seven-plus',
    },
  ],
  gcse: [
    {
      id: 'aqa',
      title: 'AQA',
      subtitle: 'Assessment & Qualifications Alliance',
      icon: BookOpen,
      accent: 'from-rose-500 to-pink-600',
      pattern: 'rings',
      link: '/practice/board/aqa',
    },
    {
      id: 'edexcel',
      title: 'Edexcel (Pearson)',
      subtitle: 'Pearson Edexcel GCSE papers',
      icon: BookOpen,
      accent: 'from-sky-500 to-cyan-600',
      pattern: 'bars',
      link: '/practice/board/edexcel',
    },
    {
      id: 'ocr',
      title: 'OCR',
      subtitle: 'Oxford, Cambridge & RSA exams',
      icon: BookOpen,
      accent: 'from-emerald-500 to-teal-600',
      pattern: 'dots',
      link: '/practice/board/ocr',
    },
  ],
};

const PracticeHub = ({ progress }) => {
  const navigate = useNavigate();
  const stage = stageOf(progress?.user);
  const boards = BOARDS_BY_STAGE[stage] || BOARDS_BY_STAGE['11-plus'];

  const stageLabel = ({
    '11-plus': '11+ Entry',
    '7-plus': '7+ Entry',
    'gcse': 'GCSE',
  })[stage] || '11+ Entry';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-white dark:from-slate-950 dark:via-indigo-950 dark:to-slate-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/welcome')}
          className="mb-4 text-slate-600 dark:text-slate-300"
          data-testid="practice-hub-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
        </Button>

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-6"
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">
            Practice Hub
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-1">
            {stageLabel} — choose your exam board
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
            Pick the assessment style you&apos;re preparing for. Each board opens its own subject papers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {boards.map((b, idx) => {
            const hasStages = Array.isArray(b.stages) && b.stages.length;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.05 + idx * 0.04 }}
              >
                <Card
                  className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`practice-board-${b.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <TileEmblem
                        icon={b.icon || Sparkles}
                        accent={b.accent}
                        pattern={b.pattern || 'dots'}
                        size="md"
                      />
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base font-bold text-slate-900 dark:text-white">{b.title}</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{b.subtitle}</p>
                      </div>
                    </div>

                    {hasStages ? (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {b.stages.map((s) => (
                          <Button
                            key={s.label}
                            variant="outline"
                            className="justify-between border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 h-auto py-2.5"
                            onClick={() => navigate(s.link)}
                            data-testid={`practice-board-${b.id}-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span className="text-left">
                              <span className="block text-sm font-semibold text-slate-900 dark:text-white">{s.label}</span>
                              <span className="block text-xs text-slate-500 dark:text-slate-400">{s.sub}</span>
                            </span>
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        className="mt-3 px-0 text-violet-700 dark:text-violet-300 hover:bg-transparent"
                        onClick={() => navigate(b.link)}
                        data-testid={`practice-board-${b.id}-open`}
                      >
                        Open practice <ChevronRight className="h-4 w-4 ml-0.5" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PracticeHub;
